import React, {ReactNode, useCallback, useEffect} from "react";
import {ConfigInterface} from "../../config";
import {useApiCall} from "../../api";
import {ChatRoomMetadata} from "../../api/repositories/chat-repository";
import {ChatRoomMetaDataContextMap, ChatMetadataContextType} from "../types/chat-metadata-context";
import moment from "moment";


export const ChatMetadataContext = React.createContext<ChatMetadataContextType>({} as ChatMetadataContextType)

export function ChatMetadataContextProvider({ children, config }:{children: React.ReactNode, config: ConfigInterface}): ReactNode {
  const {chat} = useApiCall(config)
  const [metaDataMap, setMetaDataMap] = React.useState<ChatRoomMetaDataContextMap>({})

  useEffect(() => {
    chat.getMetadata().then((list) => {
      setMetaDataMap(list.reduce((map, roomMetaData) => {
        map[roomMetaData.id] = roomMetaData
        return map
      }, {} as ChatRoomMetaDataContextMap))
    })
  }, [])

  const getMetadata = useCallback((id: string): ChatRoomMetadata|null => {
    return metaDataMap[id] ?? null
  }, [metaDataMap])

  const setFavorite = useCallback(async (id: string, isFavorite: boolean) => {
    setMetaDataMap((old) => {
      if (!old[id]) {
        return old
      }
      return {
        ...old,
        [id]: {
          ...old[id],
          is_favorite: isFavorite,
        },
      }
    })
    await chat.updateMetadata(id, {is_favorite: isFavorite})
  }, [])

  const setMuted = useCallback(async (id: string, isMuted: boolean) => {
    setMetaDataMap((old) => {
      if (!old[id]) {
        return old
      }
      return {
        ...old,
        [id]: {
          ...old[id],
          is_muted: isMuted,
        },
      }
    })
    await chat.updateMetadata(id, {is_muted: isMuted})
  }, [])

  const markChatRoomRead = useCallback(async (id: string) => {
    const date = new Date()
    setMetaDataMap((old) => {
      if (!old[id]) {
        return old
      }
      return {
        ...old,
        [id]: {
          ...old[id],
          last_opened: moment(date).format(),
        },
      }
    })
    const result = await chat.updateMetadata(id, {last_opened: moment(date).format()})

    if (result.chat_read_progress) {
      setMetaDataMap((old) => {
        if (!old[id]) {
          return old
        }
        return {
          ...old,
          [id]: {
            ...old[id],
            chat_read_progress: result.chat_read_progress
          },
        }
      })
    }
  }, [])

  return <ChatMetadataContext.Provider value={{
    getMetadata,
    setFavorite,
    setMuted,
    markChatRoomRead,
  }}>
    {children}
  </ChatMetadataContext.Provider>
}